import { z } from "zod";

import {
  boundedTextSchema,
  entityIdSchema,
  localOggAudioReferenceSchema
} from "@/domain/schemas/common";

export const sequenceItemIdSchema = entityIdSchema;
export const sequenceItemLabelSchema = boundedTextSchema(120);

export const sequenceItemSchema = z.strictObject({
  id: sequenceItemIdSchema,
  label: sequenceItemLabelSchema,
  audio: localOggAudioReferenceSchema.optional()
});

export const sequencePayloadSchema = z
  .strictObject({
    items: z.array(sequenceItemSchema).min(2),
    correctOrder: z.array(sequenceItemIdSchema).min(2)
  })
  .superRefine((payload, context) => {
    const itemIds = payload.items.map(({ id }) => id);
    const itemIdSet = new Set(itemIds);
    const correctOrderSet = new Set(payload.correctOrder);
    const seenItemIds = new Set<string>();
    const seenCorrectOrderIds = new Set<string>();

    itemIds.forEach((itemId, index) => {
      if (seenItemIds.has(itemId)) {
        context.addIssue({
          code: "custom",
          path: ["items", index, "id"],
          message: "Sequence item identifiers must be unique"
        });
      }

      seenItemIds.add(itemId);
    });

    payload.correctOrder.forEach((itemId, index) => {
      if (seenCorrectOrderIds.has(itemId)) {
        context.addIssue({
          code: "custom",
          path: ["correctOrder", index],
          message: "Correct order identifiers must be unique"
        });
      }

      if (!itemIdSet.has(itemId)) {
        context.addIssue({
          code: "custom",
          path: ["correctOrder", index],
          message: "Correct order references an unknown sequence item"
        });
      }

      seenCorrectOrderIds.add(itemId);
    });

    if (payload.correctOrder.length !== itemIds.length) {
      context.addIssue({
        code: "custom",
        path: ["correctOrder"],
        message: "Correct order must contain every sequence item exactly once"
      });
    }

    itemIds.forEach((itemId) => {
      if (!correctOrderSet.has(itemId)) {
        context.addIssue({
          code: "custom",
          path: ["correctOrder"],
          message: "Correct order is missing sequence item " + itemId
        });
      }
    });
  });

export type SequenceItem = z.infer<typeof sequenceItemSchema>;
export type SequencePayload = z.infer<typeof sequencePayloadSchema>;
